import { useReducer, useState, useMemo, useEffect, useRef } from 'react';
import './GameScreen.css';
import { gameReducer, createInitialRuntime, getPeriodName, formatTime } from '../engine/gameState';
import CharPortrait from './CharPortrait';
import { generateSmileyPortrait, imageDataToUrl } from '../engine/portrait';
import { saveState } from '../engine/save';
import { loadDefsForLocation, loadAllLocationDefs } from '../engine/loader';
import { nextStep, getConnections } from '../engine/locations';
import { buildAppearanceLines, computeItinerary, getLocationAtMinute, getTransitsDuring, absoluteMinute } from '../engine/character';

import healthDescriptions from '../data/combat/health_descriptions.json';

const HEALTH_TIERS = [
  { threshold: 1.00, key: 'unscathed', color: '#5aaa5a' },
  { threshold: 0.75, key: 'steady',    color: '#8aaa3a' },
  { threshold: 0.50, key: 'winded',    color: '#cccc44' },
  { threshold: 0.25, key: 'staggered', color: '#cc7a2a' },
  { threshold: 0.10, key: 'faltering', color: '#cc3a2a' },
  { threshold: 0,    key: 'broken',    color: '#aa1a1a' },
];

function getHealthDesc(hp, maxHp, charName, seed) {
  const pct = hp / maxHp;
  const tier = HEALTH_TIERS.find(t => pct >= t.threshold) ?? HEALTH_TIERS[HEALTH_TIERS.length - 1];
  const sentences = healthDescriptions[tier.key];
  const text = sentences[seed % sentences.length].replace(/{name}/g, charName);
  return { text, color: tier.color };
}

const DISTANCE_LABELS = {
  close:    'Close',
  mid:      'Mid',
  far:      'Far',
  very_far: 'Very Far',
};

function getCombatStateDesc(combatState, charName) {
  switch (combatState) {
    case 'clinched':        return `You and ${charName} are locked in close quarters.`;
    case 'pinned_player':   return `${charName} has you pinned to the ground.`;
    case 'holding_target':  return `You have ${charName} restrained.`;
    case 'target_downed':   return `${charName} is on the ground.`;
    default:                return `${charName} stands before you, watching for an opening.`;
  }
}

function GameScreen({ initialRuntime, onQuit }) {
  const [runtime, dispatch] = useReducer(
    gameReducer,
    null,
    () => initialRuntime ?? createInitialRuntime()
  );
  const [defs, setDefs] = useState({ locations: {}, characters: {} });
  const [loading, setLoading] = useState(true);
  const [logOpen, setLogOpen] = useState(false);
  const [movementMessages, setMovementMessages] = useState([]);
  const interactTarget = runtime.interactTarget
    ? (defs.characters[runtime.interactTarget] ?? (runtime.characters[runtime.interactTarget]?._generated ? runtime.characters[runtime.interactTarget] : null))
    : null;
  const prevMinuteRef = useRef(absoluteMinute(runtime.time));

  const smileyUrl = useMemo(() => imageDataToUrl(generateSmileyPortrait()), []);
  const playerLocation = runtime.player.location;

  // Load all location defs + current location's character defs on location change
  useEffect(() => {
    setLoading(true);
    const npcIds = Object.keys(runtime.characters);
    const generatedIds = new Set(Object.entries(runtime.characters).filter(([,c]) => c._generated).map(([id]) => id));
    Promise.all([
      loadDefsForLocation(playerLocation, npcIds, generatedIds),
      loadAllLocationDefs(),
    ]).then(([loaded, allLocations]) => {
      const mergedLocations = { ...allLocations, [playerLocation]: loaded.location };
      setDefs(prev => ({
        locations: mergedLocations,
        characters: { ...prev.characters, ...loaded.characters },
      }));
      // Compute itineraries for all characters with schedules
      for (const [id, charDef] of Object.entries(loaded.characters)) {
        if (charDef.schedule) {
          const itinerary = computeItinerary(charDef.schedule, mergedLocations, runtime.time.day);
          dispatch({ type: 'SET_CHARACTER_ITINERARY', payload: { id, itinerary } });
        }
      }
      setLoading(false);
    });
  }, [playerLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync character locations from itineraries when time changes
  useEffect(() => {
    const currentMinute = absoluteMinute(runtime.time);
    const prevMinute = prevMinuteRef.current;
    prevMinuteRef.current = currentMinute;
    if (currentMinute === prevMinute) return;

    const locationUpdates = {};
    const messages = [];

    for (const [id, charRuntime] of Object.entries(runtime.characters)) {
      if (!charRuntime.itinerary?.length) continue;

      const newLoc = getLocationAtMinute(charRuntime.itinerary, currentMinute);
      if (newLoc) locationUpdates[id] = newLoc;

      const charName = defs.characters[id]?.name ?? runtime.characters[id]?.name ?? id;
      const transitions = getTransitsDuring(charRuntime.itinerary, prevMinute, currentMinute);

      for (const transit of transitions) {
        if (transit.to === playerLocation) {
          const via = nextStep(defs.locations, playerLocation, transit.from);
          const viaName = defs.locations[via]?.name ?? via ?? '?';
          messages.push(`${charName} enters from ${viaName}.`);
        } else if (transit.from === playerLocation) {
          const via = nextStep(defs.locations, playerLocation, transit.to);
          const viaName = defs.locations[via]?.name ?? via ?? '?';
          messages.push(`${charName} leaves towards ${viaName}.`);
        }
      }
    }

    if (Object.keys(locationUpdates).length > 0 || messages.length > 0) {
      dispatch({ type: 'SYNC_CHARACTER_LOCATIONS', payload: { locations: locationUpdates, messages } });
      if (messages.length) setMovementMessages(messages);
    }
  }, [runtime.time]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (runtime.combat) return;
    if (runtime.lastCombatResult === 'dead' || runtime.lastCombatResult === 'ko') return;
    saveState(runtime);
  }, [runtime]);

  if (loading || !defs.locations[playerLocation]) {
    return <div className="game-screen game-loading">Loading...</div>;
  }

  const location = defs.locations[playerLocation];
  const connections = getConnections(location);
  const currentMinute = absoluteMinute(runtime.time);
  const charactersHere = Object.entries(runtime.characters)
    .filter(([id, c]) => {
      const loc = c.itinerary?.length
        ? getLocationAtMinute(c.itinerary, currentMinute)
        : c.location;
      return loc === playerLocation;
    })
    .map(([id, c]) => defs.characters[id] ?? (c._generated ? c : null))
    .filter(Boolean);

  function getPortrait(charData, size, className) {
    const rtData = charData?.id ? runtime.characters[charData.id] : charData;
    if (rtData?.portraitData) {
      return <img src={rtData.portraitData} alt="portrait" className={className} style={{ width: size, height: size, imageRendering: 'pixelated' }} />;
    }
    if (charData?.parts) {
      return <CharPortrait parts={charData.parts} hairColor={charData.hairColor} eyeColor={charData.eyeColor} size={size} className={className} />;
    }
    return <img src={charData?.portrait ?? smileyUrl} alt="portrait" className={className} />;
  }

  function handleStartCombat() {
    dispatch({ type: 'START_COMBAT', payload: { targetId: interactTarget.id, targetName: interactTarget.name } });
  }

  function handleFlee() {
    const charName = defs.characters[runtime.combat.targetId]?.name ?? runtime.characters[runtime.combat.targetId]?.name ?? runtime.combat.targetId;
    dispatch({ type: 'END_COMBAT', payload: { message: `You disengage from ${charName}.`, result: 'escaped' } });
    setMovementMessages([]);
  }

  function handleMove(locationId, ttt) {
    const locationName = defs.locations[locationId]?.name ?? locationId;
    dispatch({ type: 'MOVE', payload: { locationId, locationName, ttt } });
    setMovementMessages([]);
  }

  function handleAction(action) {
    dispatch({ type: 'ACTION', payload: action });
    setMovementMessages([]);
  }

  return (
    <div className="game-screen">
      <div className="game-header">
        <span className="location-name">{location.name}</span>
      </div>
      <div className="game-info">
        <span className="time-display">Day {runtime.time.day}, {formatTime(runtime.time.hour, runtime.time.minute)} · {getPeriodName(runtime.time.hour)}</span>
        <div className="player-info">
          <span className="player-name">{runtime.player.name}</span>
          <div className="stat-bar-wrap">
            <div className="stat-bar stat-bar--hp" style={{ '--pct': `${(runtime.player.hp / runtime.player.maxHp) * 100}%` }}>
              <span className="stat-bar-label">{runtime.player.hp} / {runtime.player.maxHp}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="game-body">
        {movementMessages.length > 0 && (
          <div className="movement-messages">
            {movementMessages.map((msg, i) => <p key={i} className="movement-msg">{msg}</p>)}
          </div>
        )}
        {runtime.combat ? (
          (() => {
            const combat = runtime.combat;
            const charDef = defs.characters[combat.targetId];
            const charRuntime = runtime.characters[combat.targetId];
            const charName = charDef?.name ?? charRuntime?.name ?? combat.targetId;
            const seed = absoluteMinute(runtime.time) % 5;
            const health = getHealthDesc(charRuntime.hp, charRuntime.maxHp, charName, seed);
            const stateDesc = getCombatStateDesc(combat.combatState, charName);
            const distLabel = DISTANCE_LABELS[combat.distance] ?? combat.distance;
            return (
              <div className="char-interact-wrapper">
                <div className="char-interact-panel">
                  {getPortrait(charDef ?? charRuntime, 128, 'char-portrait-large')}
                  <div className="char-interact-content">
                    {combat.lastExchange && (
                      <div className="combat-last-exchange">
                        {combat.lastExchange.map((line, i) => <p key={i} className="exchange-line">{line}</p>)}
                      </div>
                    )}
                    <p className="combat-state-desc">{stateDesc}</p>
                    <p className="combat-health" style={{ color: health.color }}>{health.text}</p>
                    <p className="combat-distance">Distance: {distLabel}</p>
                  </div>
                </div>
                <div className="combat-actions">
                  <div className="combat-row">
                    <span className="combat-row-label">Offense</span>
                    <button className="action-btn" onClick={() => dispatch({ type: 'COMBAT_ACTION', payload: { actionId: 'strike' } })}>Strike</button>
                    <button className="action-btn" onClick={() => dispatch({ type: 'COMBAT_ACTION', payload: { actionId: 'grapple' } })}>Grapple</button>
                    <button className="action-btn" onClick={() => dispatch({ type: 'COMBAT_ACTION', payload: { actionId: 'shove' } })}>Shove</button>
                  </div>
                  <div className="combat-row">
                    <span className="combat-row-label">Defense</span>
                    <button className="action-btn">Block</button>
                    <button className="action-btn">Dodge</button>
                    <button className="action-btn">Advance</button>
                    <button className="action-btn">Step Back</button>
                  </div>
                  <div className="combat-row">
                    <span className="combat-row-label">Talk</span>
                    <button className="action-btn">Taunt</button>
                    <button className="action-btn">Intimidate</button>
                    <button className="action-btn">Reason</button>
                    <button className="action-btn">Plead</button>
                  </div>
                  <div className="combat-row">
                    <span className="combat-row-label">Other</span>
                    <button className="action-btn">Inspect</button>
                    <button className="action-btn">Item</button>
                    <button className="action-btn back-btn" onClick={handleFlee}>Flee</button>
                  </div>
                </div>
              </div>
            );
          })()
        ) : interactTarget ? (
          (() => {
            const charDef = defs.characters[interactTarget.id];
            const charRt = runtime.characters[interactTarget.id];
            const charDisplay = charDef ?? (charRt?._generated ? charRt : null);
            const isGenerated = !!charRt?._generated;
            const { lines, currentSchedule } = buildAppearanceLines(
              charDef?.appearance,
              charDef?.schedule,
              runtime.time.hour
            );
            const generatedLines = isGenerated && charRt.appearance ? [
              `Hair: ${charRt.appearance.hairColorName}`,
              `Eyes: ${charRt.appearance.eyeColorName}`,
              `Chest: ${charRt.appearance.chest}`,
              `Buttocks: ${charRt.appearance.buttocks}`,
            ] : [];
            const displayLines = isGenerated ? generatedLines : lines;
            return (
              <div className="char-interact-wrapper">
                <div className="char-interact-panel">
                  {getPortrait(charDisplay, 128, 'char-portrait-large')}
                  <div className="char-interact-content">
                    <p className="observe-title">{runtime.log[runtime.log.length - 1]}</p>
                    <div className="char-appearance">
                      {displayLines.map((line, i) => <p key={i} className="appearance-line">{line}</p>)}
                      {!isGenerated && currentSchedule && (
                        <>
                          <hr className="appearance-divider" />
                          <p className="appearance-action">{currentSchedule.action}</p>
                          {runtime.characters[interactTarget.id]?.awareness && (
                            <p className="appearance-awareness">{runtime.characters[interactTarget.id].awareness}</p>
                          )}
                        </>
                      )}
                      {isGenerated && (
                        <>
                          <hr className="appearance-divider" />
                          <p className="appearance-action">They are standing around.</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="char-interact-actions">
                  <button className="action-btn" onClick={() => handleAction('talk')}>Talk</button>
                  <button className="action-btn" onClick={() => handleAction('inspect')}>Inspect</button>
                  <button className="action-btn" onClick={() => handleAction('trade')}>Trade</button>
                  <button className="action-btn combat-btn" onClick={handleStartCombat}>Attack</button>
                  <button className="action-btn back-btn" onClick={() => { dispatch({ type: 'SET_INTERACT_TARGET', payload: null }); setMovementMessages([]); }}>Back</button>
                </div>
              </div>
            );
          })()
        ) : (
          <p className="location-desc">{location.description}</p>
        )}

        <div className="room-entities">
          {!interactTarget && !runtime.combat && charactersHere.map(c => (
            <div
              key={c.id}
              className="entity-row"
              onClick={() => { dispatch({ type: 'SET_INTERACT_TARGET', payload: c.id }); dispatch({ type: 'OBSERVE', payload: c.name }); setMovementMessages([]); }}
            >
              {getPortrait(c, 40, 'character-portrait')}
              <span className="entity-name">{c.name} is here.</span>
            </div>
          ))}
        </div>
      </div>

      <div className="game-middle" />

      <div className="game-bottom">
        <div className="action-log-wrapper">
          <button className="log-toggle" onClick={() => setLogOpen(!logOpen)}>
            Log {logOpen ? '\u25BC' : '\u25B2'}
            {!logOpen && runtime.log.length > 0 && (
              <span className="log-preview">&gt; {runtime.log[runtime.log.length - 1]}</span>
            )}
          </button>
          {logOpen && (
            <div className="action-log">
              {[...runtime.log].reverse().map((entry, i) => (
                <p key={i} className="log-entry">&gt; {entry}</p>
              ))}
            </div>
          )}
        </div>

        <div className={`game-actions-bar${interactTarget || runtime.combat ? ' greyed' : ''}`}>
          <span className="bar-label">Actions</span>
          <div className="bar-buttons">
            <button className="action-btn" onClick={() => handleAction('rest')}>Rest</button>
            <button className="action-btn" onClick={() => handleAction('look')}>Look Around</button>
          </div>
        </div>
        <div className={`game-move-bar${interactTarget || runtime.combat ? ' greyed' : ''}`}>
          <span className="bar-label">Move</span>
          <div className="bar-buttons">
            {connections.map(({ id, ttt }) => (
              <button key={id} className="move-btn" onClick={() => handleMove(id, ttt)}>
                {defs.locations[id]?.name ?? id}
              </button>
            ))}
          </div>
        </div>
        <div className={`game-menu-bar${runtime.combat ? ' greyed' : ''}`}>
          <span className="bar-label">Menu</span>
          <div className="bar-buttons">
            <button className="menu-btn" onClick={() => handleAction('stats')}>Stats</button>
            <button className="menu-btn" onClick={() => handleAction('inventory')}>Inventory</button>
            <button className="menu-btn" onClick={() => handleAction('party')}>Party</button>
            <button className="menu-btn" onClick={() => handleAction('map')}>Map</button>
            <button className="menu-btn quit-btn" onClick={onQuit}>Quit</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GameScreen;
